import type { Config, PlayLike, ReactiveSource } from '../@types/editor'

export type PlaysStore<TPlay> = {
  addPlay: (config: Config) => Maybe<string>
  isPlayEmpty: (play: TPlay) => boolean
  isQuickPickFillTarget: (config: Config) => boolean
  plays: TPlay[]
  remainingPlaySlots: (config: Config) => number
  setExpandedPlay: (playId: Maybe<string>) => void
}

export const useEditorControlPanel = <TPlay extends PlayLike & { id: string }>(
  store: PlaysStore<TPlay>,
  configRef: ReactiveSource<Maybe<Config>>,
) => {
  const { scrollToCenteredPlay } = useEditorScroll()

  const config = computed(() => toValue(configRef))

  const addLineDisabled = computed(() => {
    const currentConfig = config.value
    if (!currentConfig) return true

    return (
      store.plays.some((play) => store.isPlayEmpty(play)) ||
      !store.remainingPlaySlots(currentConfig)
    )
  })

  const disabled = computed(() => {
    const currentConfig = config.value
    if (!currentConfig) return true
    return !store.remainingPlaySlots(currentConfig) && !store.isQuickPickFillTarget(currentConfig)
  })

  const addLine = async () => {
    const currentConfig = config.value
    if (!currentConfig) return

    const playId = store.addPlay(currentConfig)
    if (!playId) return

    await nextTick()
    store.setExpandedPlay(playId)
    await scrollToCenteredPlay(playId)
  }

  return { addLine, addLineDisabled, config, disabled, scrollToCenteredPlay }
}
